import { useRef, useState, type CSSProperties, type PointerEvent } from 'react';

/** Auf diesen Elementen startet keine Wisch-Geste. */
const IGNORE = 'button, input, textarea, select, a, [data-no-swipe]';
const DISTANCE_PX = 90;
const FLICK_DISTANCE_PX = 40;
const FLICK_SPEED_PX_PER_MS = 0.6;

interface Start {
  id: number;
  x: number;
  y: number;
  time: number;
}

/**
 * Wischen nach links oder oben löst `onSwipe` aus (→ nächste Person). Liefert Handler für das
 * Element und einen Style, der das Video beim Ziehen mitbewegt.
 */
export function useSwipe(onSwipe: () => void, enabled: boolean) {
  const start = useRef<Start | null>(null);
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null);

  const reset = () => {
    start.current = null;
    setOffset(null);
  };

  const style: CSSProperties | undefined = offset
    ? {
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
        opacity: 1 - Math.min(0.5, Math.hypot(offset.x, offset.y) / 600),
      }
    : undefined;

  return {
    dragging: offset !== null,
    style,
    handlers: {
      onPointerDown(event: PointerEvent<HTMLElement>) {
        if (!enabled || !event.isPrimary) return;
        if (event.target instanceof Element && event.target.closest(IGNORE)) return;
        start.current = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          time: performance.now(),
        };
      },
      onPointerMove(event: PointerEvent<HTMLElement>) {
        const origin = start.current;
        if (!origin || origin.id !== event.pointerId) return;
        const dx = Math.min(0, event.clientX - origin.x);
        const dy = Math.min(0, event.clientY - origin.y);
        if (offset === null) {
          if (Math.hypot(dx, dy) < 8) return; // noch ein Klick, keine Geste
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        setOffset(Math.abs(dx) >= Math.abs(dy) ? { x: dx, y: 0 } : { x: 0, y: dy });
      },
      onPointerUp(event: PointerEvent<HTMLElement>) {
        const origin = start.current;
        if (!origin || origin.id !== event.pointerId) return;
        const distance = Math.max(origin.x - event.clientX, origin.y - event.clientY);
        const speed = distance / Math.max(1, performance.now() - origin.time);
        reset();
        if (
          distance > DISTANCE_PX ||
          (distance > FLICK_DISTANCE_PX && speed > FLICK_SPEED_PX_PER_MS)
        ) {
          onSwipe();
        }
      },
      onPointerCancel: reset,
    },
  };
}
